import { SB } from '@play-co/replicant';
import createScheduledActions, {
  extractScheduledActionHandlers,
} from '../config/scheduledActions/createScheduledActions';
import { getLocale } from '../getters/user';
import { petMessages } from '../petChatbot/petChatbot.ruleset';
import { getJSTDateTime } from '../utils/time';
import { isGuestUser } from '../guest/guest.getters';
import ab from '../ruleset/ab';
import { preEntryInviteCreatives, preEntrySteps } from './preEntry.ruleset';
import { cancelPreEntryMessages } from './preEntry.modifiers';
import { getPreEntryPayload } from './preEntry.getters';
import { chatbotMessageTemplates } from './messageTemplates';
import {
  deliverChatbotMessage,
  type ChatbotMessageInstance,
} from './chatbot.util';

/** Message choice and timing persisted by the scheduler, not in player state. */
export const preEntryMessageSchema = SB.object({
  step: SB.tuple(preEntrySteps),
  sendAt: SB.number(),
  content: SB.union([
    SB.object({
      kind: SB.tuple(['invite']),
      creative: SB.tuple(preEntryInviteCreatives),
    }),
    SB.object({ kind: SB.tuple(['greeting']), messageId: SB.string() }),
    SB.object({ kind: SB.tuple(['reengagement']) }),
  ]),
});

/** One-shot delivery arguments registered with Replicant. */
const schema = {
  sendPreEntryMessage: preEntryMessageSchema,
};

/** One-shot pre-entry delivery, with no recurring greetings or separate deferred sends. */
export const preEntryScheduledActions = {
  schema,
  actions: extractScheduledActionHandlers(
    createScheduledActions(schema)({
      sendPreEntryMessage: (state, { step, sendAt, content }, api) => {
        // Check entry and subscription at delivery, including already-dequeued jobs and retries.
        if (
          state.loginCount !== 0 ||
          !api.chatbot.getLastKnownIsSubscribed() ||
          isGuestUser(state)
        ) {
          cancelPreEntryMessages(api);
          return;
        }

        // Removed assignments must not continue an enabled treatment.
        if (
          api.abTests.getBucketID(ab.tests.TEST_PRE_ENTRY_CHATBOT) !== 'enabled'
        ) {
          cancelPreEntryMessages(api);
          return;
        }

        // Never deliver early or let a delayed greeting escape its D2 calendar day.
        const now = api.date.now();
        if (now < sendAt) return;
        if (
          now >=
          getJSTDateTime(sendAt).plus({ days: 1 }).startOf('day').toMillis()
        ) {
          return;
        }

        // Render existing templates with a generic ball mascot; subscribers need no pet state.
        const locale = getLocale(state);
        const payload = getPreEntryPayload(content, step, locale);
        let message: ChatbotMessageInstance;
        switch (content.kind) {
          case 'invite':
            message = chatbotMessageTemplates.preEntryInvite({
              args: { creative: content.creative, locale },
              payload,
            });
            break;
          case 'greeting': {
            const greeting = petMessages[content.messageId];
            const text =
              locale === 'ja' ? greeting.text_1_jp : greeting.text_1_en;
            message = chatbotMessageTemplates.greeting({
              args: {
                message: text
                  .replaceAll(
                    '{player_name}',
                    locale === 'ja' ? 'きみ' : 'friend',
                  )
                  .replaceAll('{pet_name}', locale === 'ja' ? 'ぼく' : 'I'),
                species: 'ball',
                petName: '',
                locale,
              },
              payload,
            });
            break;
          }
          case 'reengagement':
            message = chatbotMessageTemplates.reengagement({
              args: { day: '7', species: 'ball', petName: '', locale },
              payload,
            });
            break;
        }

        // Replicant handles job completion and supplies a stable LINE retry key.
        deliverChatbotMessage(state, api, {
          receiverId: state.id,
          message,
          channel: 'oa',
        });
      },
    }),
  ),
};
